// Every policy finding rule rapid-api-auth records (auth_policy_findings),
// in plain language: what happened, and what to do about it (audit F8: the
// findings page showed bare rule names). Unknown rules fall back to their
// own name, so a new rule is never hidden.
export const FINDING_RULES = {
  domain: {
    title: "Email domain isn't bound to this connection",
    meaning: "The person's email domain isn't one of this connection's domains.",
    fix: 'If they belong to this organization, bind their domain to the connection. Otherwise they should sign in through their own organization.'
  },
  unrouted: {
    title: 'Sign-in refused: the connection reaches nobody',
    meaning: "Someone started a sign-in with a connection that isn't a public sign-in method and has no verified domain bound, so nothing should have sent them there. Cause \"testing\" means the connection is limited to named testers and this email isn't one of them.",
    fix: "Bind the organization's domain to the connection (and verify it), or make it public on Sign-in methods if it is a platform method."
  },
  public: {
    title: "Used a public sign-in method with an organization's domain",
    meaning: 'Their email domain is bound to another connection, so they should have signed in through that one.',
    fix: "Point them to their organization's own sign-in. Nothing to do if this was expected."
  },
  tenant: {
    title: 'Account from a different directory',
    meaning: 'The identity provider said this account belongs to a different Microsoft or Google organization than the one this connection is pinned to.',
    fix: "Check the connection's directory (tenant) value, or ask the person to sign in with their work account."
  },
  email_verified: {
    title: "Email address not verified",
    meaning: "The identity provider didn't confirm the person owns this email address.",
    fix: 'Ask the organization to verify the address with their identity provider.'
  },
  no_email: {
    title: 'No email address sent',
    meaning: "The identity provider didn't send an email address for this person.",
    fix: 'Ask the organization to release the email claim to EducationXR.'
  },
  originating_email: {
    title: 'Signed in as a different address',
    meaning: 'The person started with one email address and came back from the identity provider as another. Recorded only; never blocks.',
    fix: 'Usually a personal account signed in on the same browser. Nothing to do unless it repeats.'
  },
  credential_expired: {
    title: "Sign-in stopped: the connection's credential has expired",
    meaning: "The client secret or certificate EducationXR uses for this connection is past its expiry date.",
    fix: "Put a new secret or certificate in the connection's Credentials."
  },
  org_claim_no_match: {
    title: 'No organization rule matched',
    meaning: "The person's claims matched none of the rules of the organizations linked to this connection.",
    fix: "Check the rules under the connection's Domains & organizations, and the claim values in the finding."
  },
  idp_logout_skipped: {
    title: 'Identity provider sign-out skipped',
    meaning: "Signing out of EducationXR didn't also end the identity provider session.",
    fix: 'Usually no sign-out endpoint is published. Check the connection\'s Sign-out settings if it should.'
  },
  // SSO link confirmation (plan 3.4): a public sign-in button met an
  // existing account it isn't connected to, so auth held the sign-in until
  // the person proved the account (password, emailed code, or a sign-in
  // method the account already has).
  link_unconfirmed: {
    title: 'Asked to confirm a new sign-in method',
    meaning: "A public sign-in button (like Google) was used for an existing account it isn't connected to yet, so the person was asked to show the account is theirs first.",
    fix: 'Nothing to do. If it repeats on one account and is never confirmed, someone may be trying to get in.'
  },
  link_confirm_failed: {
    title: "Couldn't confirm a new sign-in method",
    meaning: "Someone tried to confirm an account (password, emailed code or another sign-in) and it didn't match.",
    fix: 'Nothing unless it repeats. Many on one account may mean someone is trying to get in: check its sign-in history.'
  },
  // auth's SSO return check (2026-10-21): recorded, not refused, until
  // POLICY_ENFORCE_SSO_RETURN is switched on.
  sso_return: {
    title: "Sign-in asked to return somewhere that isn't EducationXR",
    meaning: "A sign-in named a return address that is not one of EducationXR's own (an app, the admin or the web viewer), so after signing in the person would have been sent elsewhere. Recorded only; the sign-in went ahead.",
    fix: "If the address is one of ours, it needs adding to auth's list. If not, someone may be sharing a crafted sign-in link: check where it came from."
  },
  sso_required: {
    title: 'Password refused: single sign-on is required',
    meaning: "Someone tried a password for a domain that requires single sign-on.",
    fix: "Expected. They need to use their organization's sign-in."
  },
  notification_no_recipients: {
    title: 'An alert had nobody configured to receive it',
    meaning: 'An SSO alert went only to the fallback staff list because no recipients are set.',
    fix: 'Add alert recipients for the organization, or platform recipients on the Jobs page.'
  },
  domain_reverify: {
    title: 'Daily domain check changed a domain',
    meaning: "The daily DNS check found a domain's TXT record present or missing and updated its state.",
    fix: 'If it went at risk, ask the organization to put the TXT record back before the deadline.'
  },
  domain_reverify_unknown: {
    title: "Daily domain check couldn't reach DNS",
    meaning: "The DNS lookup for a domain failed, so its state was left as it was.",
    fix: 'Nothing unless it repeats; then check the domain resolves.'
  },
  sso_plan: {
    title: 'Single sign-on used outside the plan',
    meaning: "An organization admin tried to manage single sign-on, but it isn't in their plan.",
    fix: "Turn on single sign-on in the organization's plan if they should have it."
  },
  session_idle: {
    title: 'Session idle too long',
    meaning: 'An admin session passed its idle limit.',
    fix: 'Nothing to do.'
  },
  session_max: {
    title: 'Session open too long',
    meaning: 'An admin session passed its maximum length.',
    fix: 'Nothing to do.'
  },
  origin_lost: {
    title: 'Where a sign-in started was lost',
    meaning: "EducationXR couldn't recall which page or device a sign-in started from.",
    fix: 'Nothing unless it repeats.'
  },
  nonce: {
    title: "A sign-in response couldn't be verified",
    meaning: "The identity provider's answer didn't match the sign-in EducationXR started.",
    fix: 'Usually an old or reused sign-in tab. Investigate if it repeats for many people.'
  },
  consent_accepted: {
    title: 'Microsoft consent approved',
    meaning: "An IT administrator approved EducationXR in their Microsoft organization.",
    fix: 'Bind the organization\'s domain to the connection so it goes live.'
  },
  backchannel_logout_accepted: {
    title: 'Identity provider signed someone out',
    meaning: 'The identity provider told EducationXR to end a session.',
    fix: 'Nothing to do.'
  },
  client: {
    title: 'Staff API called from a non-admin app',
    meaning: 'A staff-only request came from an app other than the admin site.',
    fix: 'Investigate if it wasn\'t you testing.'
  }
}

export const findingRule = rule => FINDING_RULES[rule] || { title: rule, meaning: '', fix: '' }

export const FINDING_RULE_OPTIONS = Object.keys(FINDING_RULES)
  .map(value => ({ value, label: FINDING_RULES[value].title }))
  .sort((a, b) => a.label.localeCompare(b.label))

// A finding's detail object as label/value pairs: camelCase and snake_case
// keys in words, lists joined, objects as compact JSON.
export const detailPairs = detail =>
  Object.entries(detail || {}).map(([key, value]) => {
    const label = key.replace(/_/g, ' ').replace(/([a-z])([A-Z])/g, '$1 $2').toLowerCase()
    let text
    if (value === null || value === undefined || value === '') text = '(none)'
    else if (Array.isArray(value)) text = value.length ? value.map(v => (typeof v === 'object' ? JSON.stringify(v) : String(v))).join(', ') : '(none)'
    else if (typeof value === 'object') text = JSON.stringify(value)
    else text = String(value)
    return { key, label: label.charAt(0).toUpperCase() + label.slice(1), text }
  })
